import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import io.janni.wedding.components.layouts.MarkdownLayout as _io_janni_wedding_components_layouts_MarkdownLayout
import io.janni.wedding.components.layouts.PageLayout as _io_janni_wedding_components_layouts_PageLayout
import io.janni.wedding.pages.HomePage as _io_janni_wedding_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Hochzeit - Lisa & Janni"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "io.janni.wedding.components.layouts.MarkdownLayout",
            parentLayoutId = "io.janni.wedding.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                io.janni.wedding.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _io_janni_wedding_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "io.janni.wedding.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _io_janni_wedding_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "io.janni.wedding.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                io.janni.wedding.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _io_janni_wedding_pages_HomePage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", io.janni.wedding.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", io.janni.wedding.SubheadlineTextStyle)
        ctx.theme.registerStyle("footer", io.janni.wedding.components.sections.FooterStyle)
        ctx.theme.registerStyle("hero-container", io.janni.wedding.components.sections.HeroContainerStyle)
        ctx.theme.registerStyle("content-container", io.janni.wedding.components.sections.ContentContainerStyle)
        ctx.theme.registerStyle("title-container", io.janni.wedding.components.sections.TitleContainerStyle)
        ctx.theme.registerStyle("content-column", io.janni.wedding.components.sections.ContentColumnStyle)
        ctx.theme.registerStyle("title-text", io.janni.wedding.components.sections.TitleTextStyle)
        ctx.theme.registerStyle("nav-header", io.janni.wedding.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("markdown", io.janni.wedding.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", io.janni.wedding.components.layouts.PageContentStyle)
        ctx.theme.registerVariant("-circle", io.janni.wedding.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", io.janni.wedding.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", io.janni.wedding.components.sections.SideMenuSlideInAnim)
        io.janni.wedding.initSiteStyles(ctx)
        io.janni.wedding.initColorMode(ctx)
        io.janni.wedding.initStyles(ctx)
        io.janni.wedding.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        io.janni.wedding.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
